import type { PlatformTheme, PlatformThemeColors } from './types';
import { buildThemeGradients } from './presets';
import { ensureReadableText, hexToRgba, blendHexColors, relativeLuminance, isLightColor } from './contrast';
import { getShellChromeVars } from './chrome';

export type ThemeStyle = Record<string, string | number | undefined>;
export { isLightColor } from './contrast';
export { getShellChromeVars } from './chrome';

function parseAccentRgb(hex: string): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export function applyPlatformTheme(theme: PlatformTheme): void {
  const root = globalThis.document?.documentElement;
  if (!root || typeof root.style?.setProperty !== 'function') return;
  const c = theme.colors;
  const g = theme.gradients;

  const set = (name: string, value: string | undefined) => {
    if (value) root.style.setProperty(name, value);
  };

  // Glass (translucent + backdrop-blur) / glow (neon box-shadow halo) are opt-in per-theme via
  // cssVariables (see the PlatformTheme.cssVariables doc comment in types.ts) — every preset that
  // doesn't set platform-effect renders exactly as before this was added.
  const effectVars = theme.cssVariables ?? {};
  const effect = effectVars['platform-effect'];
  const isGlass = effect === 'glass';
  const isGlow = effect === 'glow';
  const glassAlpha = isGlass
    ? Math.max(0.15, Math.min(0.95, Number.parseFloat(effectVars['platform-glass-alpha'] ?? '0.55') || 0.55))
    : 1;
  const glassBlur = isGlass ? (effectVars['platform-glass-blur'] || '18px') : 'none';

  const surfacePaint = isGlass ? hexToRgba(c.surface, glassAlpha) : c.surface;
  const surface2Paint = isGlass ? hexToRgba(c.surface2, glassAlpha) : c.surface2;
  const surface3Paint = isGlass ? hexToRgba(c.surface3, glassAlpha) : c.surface3;
  const headerPaint = isGlass ? hexToRgba(c.headerBackground, glassAlpha) : c.headerBackground;
  const sidebarPaint = isGlass ? hexToRgba(c.sidebarBackground, glassAlpha) : c.sidebarBackground;

  // Contrast-safe text: computed against what the surface actually renders as once blended with
  // the page background (glass) or the surface color itself (classic/glow) — not blindly trusting
  // the theme-authored text color, which guards custom/user-created themes as well as presets.
  const textBasis = isGlass ? blendHexColors(c.background, c.surface, glassAlpha) : c.surface;
  const text = ensureReadableText(textBasis, c.text);
  const textMutedPreferred = c.textMuted?.startsWith('#') ? c.textMuted : undefined;
  const textMuted = textMutedPreferred
    ? (relativeLuminance(textBasis) > 0.5 === relativeLuminance(textMutedPreferred) > 0.5 ? c.textMuted : text)
    : text;

  set('--color-bg', c.background);
  set('--color-surface', surfacePaint);
  set('--color-surface-2', surface2Paint);
  set('--color-surface-3', surface3Paint);
  set('--color-border', c.border);
  set('--color-text', text);
  set('--color-text-muted', textMuted);
  set('--color-text-dim', c.textDim);
  set('--color-accent', c.accentPrimary);
  set('--color-accent-hover', c.accentHover);
  set('--color-accent-rgb', parseAccentRgb(c.accentPrimary));
  set('--color-success', c.success);
  set('--color-warning', c.warning);
  set('--color-error', c.error);
  set('--color-header-bg', headerPaint);
  set('--color-sidebar-bg', sidebarPaint);

  for (const [name, value] of Object.entries(getShellChromeVars(c, textMuted))) {
    set(name, value);
  }

  set('--platform-gradient-primary', g.primary);
  set('--platform-gradient-secondary', g.secondary);
  set('--platform-gradient-background', g.background);
  set('--platform-gradient-banner', g.banner);
  set('--platform-gradient-avatar', g.avatar);

  // backdrop-filter defaults to 'none' for every theme that doesn't opt into glass (zero visual/
  // perf cost for the other presets). The glow shadows are always a real value — a subtle
  // elevation tint normally, a bigger neon halo for Glow presets — never the literal string
  // 'none', since that can't be dropped into a comma-separated box-shadow list.
  set('--platform-glass-blur', glassBlur);
  set(
    '--platform-glow-surface',
    isGlow
      ? (effectVars['platform-glow-surface'] || `0 0 16px ${hexToRgba(c.accentPrimary, 0.35)}, 0 0 3px ${hexToRgba(c.accentPrimary, 0.5)}`)
      : `0 1px 4px rgba(0, 0, 0, 0.18)`,
  );
  set(
    '--platform-glow-accent',
    isGlow
      ? (effectVars['platform-glow-accent'] || `0 4px 16px ${hexToRgba(c.accentPrimary, 0.5)}, 0 0 10px ${hexToRgba(c.accentPrimary, 0.6)}`)
      : `0 4px 16px ${hexToRgba(c.accentPrimary, 0.35)}`,
  );
  // Reusable accent alpha tints — precomputed rather than via CSS color-mix() (which silently
  // drops its whole declaration on iOS Safari < 16.4) — for hover/active tints that should
  // follow the theme's own accent instead of staying a fixed hardcoded color per platform.
  set('--platform-accent-tint-subtle', hexToRgba(c.accentPrimary, 0.08));
  set('--platform-accent-tint-hover', hexToRgba(c.accentPrimary, 0.14));
  set('--platform-accent-tint-medium', hexToRgba(c.accentPrimary, 0.2));
  set('--platform-accent-tint-border', hexToRgba(c.accentPrimary, 0.35));

  root.dataset.platformTheme = theme.name;
  root.dataset.themeMode = isLightColor(c.background) ? 'light' : 'dark';
  root.dataset.platformEffect = effect === 'glass' || effect === 'glow' ? effect : 'classic';

  // Escape hatch: any cssVariables entry not already consumed above is applied verbatim as
  // --<key>, letting a theme fine-tune or add one-off variables without a new recognized key.
  const consumed = new Set([
    'platform-effect', 'platform-glass-alpha', 'platform-glass-blur',
    'platform-glow-surface', 'platform-glow-accent',
  ]);
  for (const [key, value] of Object.entries(effectVars)) {
    if (!consumed.has(key) && value) root.style.setProperty(`--${key}`, value);
  }
}

export function normalizeTheme(raw: PlatformTheme & { _id?: unknown }): PlatformTheme {
  const colors = { ...raw.colors } as PlatformThemeColors;
  const gradients = raw.gradients?.banner ? raw.gradients : buildThemeGradients(colors);
  return {
    ...raw,
    _id: raw._id != null ? String(raw._id) : undefined,
    colors,
    gradients,
  };
}

export function getThemeKey(theme: PlatformTheme): string {
  return theme._id ? `custom:${theme._id}` : `builtin:${theme.name}`;
}

export type BannerFit = 'cover' | 'contain' | 'fill';
export interface BannerPosition { x: number; y: number }

const BANNER_SIZE_BY_FIT: Record<BannerFit, string> = {
  cover: 'cover',
  contain: 'contain',
  fill: '100% 100%',
};

export function getBannerStyle(
  theme: PlatformTheme | null | undefined,
  imageUrl?: string,
  position?: BannerPosition,
  fit: BannerFit = 'cover',
): ThemeStyle {
  if (imageUrl) {
    const x = Math.min(100, Math.max(0, position?.x ?? 50));
    const y = Math.min(100, Math.max(0, position?.y ?? 50));
    return {
      backgroundImage: `url(${imageUrl})`,
      backgroundSize: BANNER_SIZE_BY_FIT[fit] ?? 'cover',
      backgroundPosition: `${x}% ${y}%`,
      backgroundRepeat: 'no-repeat',
    };
  }
  const gradient = theme?.gradients?.banner ?? theme?.gradients?.primary;
  return gradient ? { background: gradient } : {};
}

export function getAvatarStyle(theme: PlatformTheme | null | undefined, imageUrl?: string): ThemeStyle {
  if (imageUrl) return {};
  const gradient = theme?.gradients?.avatar ?? theme?.gradients?.secondary;
  return gradient ? { background: gradient } : {};
}

export const DEFAULT_CUSTOM_COLORS: PlatformThemeColors = {
  background: '#0d0d0d',
  surface: '#1a1a1a',
  surface2: '#222222',
  surface3: '#2a2a2a',
  border: '#2a2a2a',
  text: '#f0f0f0',
  textMuted: '#888888',
  textDim: '#555555',
  accentPrimary: '#00b894',
  accentSecondary: '#6c5ce7',
  accentHover: '#00a381',
  headerBackground: '#1a1a1a',
  sidebarBackground: '#1a1a1a',
  success: '#00b894',
  warning: '#fdcb6e',
  error: '#d63031',
};

export function createDraftTheme(
  name = 'My Theme',
  accentPrimary = '#00b894',
  accentSecondary = '#6c5ce7',
): PlatformTheme {
  const colors = { ...DEFAULT_CUSTOM_COLORS, accentPrimary, accentSecondary, accentHover: accentPrimary };
  return { name, description: 'Custom theme', colors, gradients: buildThemeGradients(colors) };
}
